import { forwardRef, useEffect, useImperativeHandle, useState } from "react";
import { Button, Col, Radio, Row, Select } from "antd";
import { GlobalTable } from "../../../global/components/GlobalTable";
import useGet from "../../../global/hooks/useGet";
import { IoIosCheckmarkCircleOutline } from "react-icons/io";
import { HiArrowPath } from "react-icons/hi2";
import { formatUTCTime } from "../../../global/utils/unixToLocalTime";
import SortIcon from "../../../global/components/SortIcon";

const initialFilterState = {
  GiftCardNo: undefined,
  Used: undefined,
};

const usedOptions = [
  { value: 1, label: "Yes" },
  { value: 0, label: "No" },
];

const getGiftCardColumns = (
  selectedRow,
  handleRadioChange,
  params,
  setParams,
) => {
  const modParams = params ?? {};
  return [
    {
      title: "Prepurchased License",
      dataIndex: "GiftCardName",
      key: "GiftCardName",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="GiftCardNo"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "GiftCardNo",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
      render: (_, record) => record?.GiftCardName || "",
    },
    {
      title: "Used",
      dataIndex: "Used",
      key: "Used",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="Used"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "Used",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
      render: (value) =>
        value === 1 || value === "1" || value === true || value === "Yes"
          ? "Yes"
          : "No",
    },
    {
      title: "Used MAC",
      dataIndex: "UsedMac",
      key: "UsedMac",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="UsedMac"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "UsedMac",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
    },
    {
      title: "Used Time",
      dataIndex: "UsedTime",
      key: "UsedTime",
      sorter: (a, b) => () => {},
      sortIcon: () => (
        <SortIcon
          sortOrder={modParams.order}
          columnKey="UsedTime"
          sortBy={modParams.sort_by}
        />
      ),
      onHeaderCell: () => ({
        onClick: () =>
          setParams((prev) => ({
            ...prev,
            sort_by: "UsedTime",
            order: prev.order === "desc" ? "asc" : "desc",
          })),
      }),
      render: (text) => (text ? formatUTCTime(text) : ""),
    },
    {
      title: "",
      key: "select",
      width: 50,
      align: "center",
      render: (_, record) =>
        record.Used === 1 ||
        record.Used === "1" ||
        record.Used === true ||
        record.Used === "Yes" ? null : (
          <Radio
            checked={selectedRow?.ItemNo === record.ItemNo}
            onChange={() => handleRadioChange(record)}
          />
        ),
    },
  ];
};

export const GiftCardItemsForm = forwardRef(({ dealerNo }, ref) => {
  const [listData, setListData] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [filter, setFilter] = useState(initialFilterState);
  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const [params, setParams] = useState({
    limit: 10,
    page: 1,
    sort_by: "ItemNo",
    order: "desc",
  });

  const queryObj = {
    ...params,
    ...(dealerNo != null && dealerNo !== "" ? { DealerNo: dealerNo } : {}),
  };
  const query = new URLSearchParams(queryObj).toString();
  const url =
    dealerNo != null && dealerNo !== ""
      ? `${process.env.REACT_APP_API_URL}/api/devices/gift-card-items/?${query}`
      : null;

  const { data, loading, refetch } = useGet(url);

  const paginationConfig = {
    current: pagination.current,
    pageSize: pagination.pageSize,
    total: data?.count ?? data?.total ?? pagination.total,
    showSizeChanger: true,
    pageSizeOptions: [10, 20, 50, 100],
    showTotal: (total, range) => `Total ${total} items ${range[0]}-${range[1]}`,
    onChange: (page, pageSize) => {
      setPagination((prev) => ({
        ...prev,
        current: page,
        pageSize: pageSize,
      }));
      setParams((prev) => ({
        ...prev,
        limit: pageSize,
        page: page,
      }));
    },
  };

  const prepurchasedQuery = new URLSearchParams({
    sort_by: "GiftCardName",
    order: "asc",
  }).toString();
  const prepurchasedUrl = `${process.env.REACT_APP_API_URL}/api/giftcards/?${prepurchasedQuery}`;
  const { data: prepurchasedData } = useGet(prepurchasedUrl);
  const prepurchasedOptions =
    prepurchasedData?.data?.map((item) => ({
      label: item.GiftCardName,
      value: item.GiftCardNo,
    })) ?? [];

  useImperativeHandle(ref, () => ({
    getSelectedRows: () => (selectedRow ? [selectedRow] : []),
  }));

  const handleRadioChange = (record) => {
    setSelectedRow(record);
  };

  useEffect(() => {
    if (data && !loading) {
      const newData = (data?.data ?? []).map((el, index) => ({
        ...el,
        key: el?.ItemNo ?? el?.id ?? index,
      }));
      setListData(newData);
      const totalCount = data?.count ?? data?.total;
      if (typeof totalCount === "number") {
        setPagination((prev) => ({ ...prev, total: totalCount }));
      }
    }
  }, [data, loading]);

  const handleFilter = () => {
    const filterDict = {};
    for (const key in filter) {
      if (
        filter[key] !== null &&
        filter[key] !== "" &&
        filter[key] !== undefined
      ) {
        filterDict[key] = filter[key];
      }
    }
    const isEmpty = JSON.stringify(filterDict) === "{}";
    if (isEmpty) {
      return;
    }
    setPagination((prev) => ({ ...prev, current: 1 }));
    setParams((prev) => ({
      ...prev,
      ...filterDict,
      page: 1,
    }));
    refetch();
  };

  const handleReset = () => {
    setFilter(initialFilterState);
    setPagination({
      current: 1,
      pageSize: 10,
      total: 0,
    });
    setParams({
      limit: 10,
      page: 1,
      sort_by: "ItemNo",
      order: "desc",
    });
    refetch();
  };

  if (dealerNo == null || dealerNo === "") {
    return (
      <div className="py-4 text-gray-500">
        No dealer context. Please ensure you are logged in as a dealer.
      </div>
    );
  }

  return (
    <div className="mt-3">
      <div className="mt-3">
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              showSearch
              allowClear
              placeholder="Prepurchased License"
              options={prepurchasedOptions}
              value={filter.GiftCardNo}
              onChange={(value) =>
                setFilter((prev) => ({ ...prev, GiftCardNo: value }))
              }
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} sm={24} md={12} lg={8} xl={6}>
            <Select
              allowClear
              placeholder="Used"
              options={usedOptions}
              value={filter.Used}
              onChange={(value) =>
                setFilter((prev) => ({
                  ...prev,
                  Used: value != null ? Number(value) : value,
                }))
              }
              style={{ width: "100%" }}
            />
          </Col>
        </Row>
      </div>

      <div className="flex items-center my-4 flex-nowrap gap-2">
        <Button
          type="primary"
          className="mr-2 w-[104px] h-[30px] font-medium"
          onClick={handleFilter}
        >
          <IoIosCheckmarkCircleOutline className="!text-[14px] font-medium" />
          Apply
        </Button>
        <Button
          className="reset-button w-[104px] h-[30px] font-medium"
          onClick={handleReset}
        >
          <HiArrowPath className="!text-[14px] font-medium" />
          Reset
        </Button>
      </div>

      <GlobalTable
        columns={getGiftCardColumns(
          selectedRow,
          handleRadioChange,
          params,
          setParams,
        )}
        dataSource={listData}
        pagination={paginationConfig}
        loading={loading}
      />
    </div>
  );
});
